import { type ReactElement, useCallback, useEffect, useState } from 'react';
import type { ApiError, FleetOrganizationView, FleetVehicleAssetView } from '@vello/api-client';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerSection, EmptyCell, Field, Notice, Panel, PanelHead, Table, Text, TextInput } from '@vello/web-theme';
import {
  emptyFleetForm, fleetAcceptsDrivers, fleetAdminActions, fleetDriverRows, normalizeNewFleet, resolveLogin,
  type FleetDriverRow, type FleetFormIssue, type LoginCandidate, type LoginResolution, type NewFleetForm,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { DrawerStack } from './drawer-stack';
import { IdRef } from './code-ref';
import { ScopeMarketNotice, useScopeMarket } from './scope-market';

type Msg = { tone: 'success' | 'danger' | 'warning'; text: string; link?: string };
const ORG_TONE: Readonly<Record<string, 'neutral' | 'success' | 'warning' | 'danger'>> = { onboarding: 'neutral', active: 'success', suspended: 'warning', closed: 'danger' };

/**
 * D849 (P0-010) - Empresas › **Frotas**: the fleet organizations, a way to create one ("Nova frota": name, the header scope's market, the
 * owner), to give it an owner (the BUSINESS login that holds `FLEET_MANAGER` on that fleet) and to see and link its drivers and vehicles.
 * Linking a driver changes how that driver is paid (the fleet's settlement statement and payout split), so it asks first, in the console's
 * own confirmation sheet, naming that change.
 */
export function FleetsPanel(): ReactElement {
  const { runtime, grants, markets, me } = useAdmin();
  const { t } = useT();
  const { v, fail, explain: said } = useVocab();
  const { confirm } = useConsole();
  const countryName = useCountryName();
  const { market: marketId, choices } = useScopeMarket();
  const can = fleetAdminActions(grants);
  const market = markets.find((m) => m.id === marketId);

  const [fleets, setFleets] = useState<readonly FleetOrganizationView[] | undefined>(undefined);
  const [refresh, setRefresh] = useState(0);
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<NewFleetForm>(emptyFleetForm);
  const [issues, setIssues] = useState<readonly FleetFormIssue[]>([]);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | undefined>(undefined);

  const explain = useCallback((e: ApiError): string => said(e) ?? t('pob.refused', { code: fail(e) }), [t, said, fail]);

  useEffect(() => {
    let cancelled = false;
    void runtime.fleetManagement.listOrganizations().then((res) => {
      if (!cancelled) setFleets(res.ok ? res.value.organizations : []);
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, refresh]);

  /** Give the fleet an owner: find or create the BUSINESS login, then grant it `FLEET_MANAGER` on this fleet. */
  const giveOwner = useCallback(
    async (org: FleetOrganizationView, owner: LoginCandidate): Promise<{ ok: true; link?: string } | { ok: false; text: string }> => {
      const listed = await runtime.operatorsAdmin.list();
      if (!listed.ok) return { ok: false, text: explain(listed.error) };
      const country = org.scope.countryCode ?? me?.countryCode ?? '';
      const zone = markets.find((m) => m.id === org.scope.marketId)?.timezone ?? me?.timezone;
      const resolved: LoginResolution = resolveLogin(owner, listed.value.operators, {
        realm: 'BUSINESS', countryCode: country.toUpperCase(), ...(me?.locale !== undefined ? { locale: me.locale } : {}), ...(zone !== undefined ? { timezone: zone } : {}), reasonCode: 'operator_onboarding',
      });
      if (resolved.kind === 'refused') return { ok: false, text: t(`pob.fleet.owner.refused.${resolved.reason}`) };
      let userId: string;
      let link: string | undefined;
      if (resolved.kind === 'existing') userId = resolved.userId;
      else {
        const made = await runtime.operatorsAdmin.invite(resolved.invite);
        if (!made.ok) return { ok: false, text: explain(made.error) };
        userId = made.value.operator.userId;
        if (made.value.invitation.delivery === 'MANUAL') link = made.value.invitation.url;
      }
      const granted = await runtime.operatorsAdmin.grant(userId, { role: 'FLEET_MANAGER', scopeType: 'FLEET', scopeId: org.organizationId, reasonCode: 'operator_onboarding' });
      // A grant that is already there is the state we wanted.
      if (!granted.ok && granted.error.reasonCode !== 'OPERATOR_GRANT_EXISTS') return { ok: false, text: explain(granted.error) };
      return { ok: true, ...(link !== undefined ? { link } : {}) };
    },
    [runtime, explain, markets, me, t],
  );

  const openCreate = (): void => {
    setForm(emptyFleetForm());
    setIssues([]);
    setMsg(undefined);
    setCreating(true);
  };

  const create = async (): Promise<void> => {
    const normalized = normalizeNewFleet(form, market);
    if (!normalized.ok) {
      setIssues(normalized.error);
      return;
    }
    setIssues([]);
    setBusy(true);
    const made = await runtime.fleetManagement.createOrganization({ displayName: normalized.value.displayName, scope: normalized.value.scope });
    if (!made.ok) {
      setBusy(false);
      setMsg({ tone: 'danger', text: t('pob.fleet.refused', { code: fail(made.error) }) });
      return;
    }
    const org = made.value.organization;
    let next: Msg = { tone: 'success', text: t('pob.fleet.created') };
    const owner = normalized.value.owner;
    if (owner !== undefined && can.mayInviteOwner) {
      const given = await giveOwner(org, owner);
      next = given.ok
        ? given.link !== undefined
          ? { tone: 'warning', text: t('pob.fleet.owner.done.manual'), link: given.link }
          : { tone: 'success', text: t('pob.fleet.created.owner', { email: owner.email }) }
        : { tone: 'warning', text: t('pob.fleet.created.ownerFailed', { reason: given.text.replace(/[.\s]+$/, '') }) };
    }
    setBusy(false);
    setCreating(false);
    setMsg(next);
    setRefresh((n) => n + 1);
  };

  const open = fleets?.find((f) => f.organizationId === openId);
  const marketLabel = (org: FleetOrganizationView): string => {
    const m = markets.find((x) => x.id === org.scope.marketId);
    return m !== undefined ? m.name : org.scope.countryCode !== undefined ? countryName(org.scope.countryCode) : '';
  };
  const fieldHint = (issue: FleetFormIssue): string | undefined => (issues.includes(issue) ? t(`pob.fleet.issue.${issue}`) : undefined);
  const setF = (k: keyof NewFleetForm) => (value: string): void => setForm((f) => ({ ...f, [k]: value }));

  return (
    <>
      <Panel padded={false}>
        <PanelHead title={t('companies.fleets')} actions={can.mayCreate ? <Button variant="secondary" onClick={openCreate} data-testid="fleet-new">{t('pob.fleet.new')}</Button> : undefined} />
        {msg !== undefined && !creating && (
          <div>
            <Notice tone={msg.tone}>
              {msg.text}
              {msg.link !== undefined && <Text as="code" mono block data-testid="fleet-manual-link">{msg.link}</Text>}
            </Notice>
          </div>
        )}
        <Table
          columns={[
            { key: 'name', header: t('companies.col.merchant'), render: (o: FleetOrganizationView) => o.displayName },
            { key: 'status', header: t('companies.col.status'), render: (o: FleetOrganizationView) => <Badge tone={ORG_TONE[o.status] ?? 'neutral'}>{v('fleet.org_status', o.status)}</Badge> },
            { key: 'market', header: t('pob.fleet.col.market'), render: (o: FleetOrganizationView) => marketLabel(o) },
            { key: 'country', header: t('companies.col.country'), render: (o: FleetOrganizationView) => (o.scope.countryCode !== undefined ? countryName(o.scope.countryCode) : '') },
          ]}
          rows={fleets ?? []}
          rowKey={(o) => o.organizationId}
          onRowClick={(o) => setOpenId(o.organizationId)}
          selectedKey={openId}
          empty={<EmptyCell>{fleets === undefined ? t('companies.loading') : t('companies.fleets.empty')}</EmptyCell>}
        />
      </Panel>

      <Drawer open={creating} onClose={() => setCreating(false)} labelledBy="fleet-new-title">
        <DrawerHeader titleId="fleet-new-title" title={t('pob.fleet.new')} subtitle={t('pob.fleet.new.subtitle')} onClose={() => setCreating(false)} closeLabel={t('ui.close')} />
        <DrawerBody>
          <DrawerStack>
            {msg !== undefined && creating && <Notice tone={msg.tone}>{msg.text}</Notice>}
            <Field label={t('pob.fleet.name')} htmlFor="fleet-name" hint={fieldHint('name')}><TextInput id="fleet-name" value={form.name} onChange={(e) => setF('name')(e.target.value)} /></Field>
            {market === undefined ? (
              <ScopeMarketNotice choices={choices} />
            ) : (
              <Field label={t('pob.fleet.market')} htmlFor="fleet-market" hint={fieldHint('market') ?? t('pob.fleet.market.hint')}><TextInput id="fleet-market" readOnly value={`${market.name} (${market.id})`} /></Field>
            )}
            <DrawerSection title={t('pob.fleet.owner.section')}>
              {can.mayInviteOwner ? (
                <DrawerStack gap={3}>
                  <Text role="caption" tone="secondary" as="p">{t('pob.fleet.owner.hint')}</Text>
                  <Field label={t('pob.fleet.owner.email')} htmlFor="fleet-owner-email" hint={fieldHint('ownerEmail')}><TextInput id="fleet-owner-email" type="email" autoComplete="off" value={form.ownerEmail} onChange={(e) => setF('ownerEmail')(e.target.value)} /></Field>
                  <Field label={t('pob.fleet.owner.name')} htmlFor="fleet-owner-name" hint={fieldHint('ownerName') ?? t('pob.fleet.owner.newOnly')}><TextInput id="fleet-owner-name" value={form.ownerName} onChange={(e) => setF('ownerName')(e.target.value)} /></Field>
                  <Field label={t('pob.fleet.owner.phone')} htmlFor="fleet-owner-phone" hint={fieldHint('ownerPhone') ?? t('pob.fleet.owner.newOnly')}><TextInput id="fleet-owner-phone" type="tel" className="tnum" value={form.ownerPhone} onChange={(e) => setF('ownerPhone')(e.target.value)} /></Field>
                </DrawerStack>
              ) : (
                <Notice tone="neutral">{t('pob.fleet.owner.noPermission')}</Notice>
              )}
            </DrawerSection>
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={() => setCreating(false)}>{t('ui.cancel')}</Button>
          <Button variant="primary" disabled={busy || market === undefined} onClick={() => void create()} data-testid="fleet-create">{t('pob.fleet.create')}</Button>
        </DrawerFooter>
      </Drawer>

      <Drawer open={open !== undefined} onClose={() => setOpenId(undefined)} labelledBy="fleet-detail-title">
        {open !== undefined && (
          <FleetDetail
            org={open}
            canManage={can.mayManage}
            canInviteOwner={can.mayInviteOwner}
            marketLabel={marketLabel(open)}
            onClose={() => setOpenId(undefined)}
            giveOwner={giveOwner}
            confirm={confirm}
            explain={explain}
          />
        )}
      </Drawer>
    </>
  );
}

function FleetDetail({ org, canManage, canInviteOwner, marketLabel, onClose, giveOwner, confirm, explain }: {
  org: FleetOrganizationView;
  canManage: boolean;
  canInviteOwner: boolean;
  marketLabel: string;
  onClose: () => void;
  giveOwner: (org: FleetOrganizationView, owner: LoginCandidate) => Promise<{ ok: true; link?: string } | { ok: false; text: string }>;
  confirm: ReturnType<typeof useConsole>['confirm'];
  explain: (e: ApiError) => string;
}): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const [assets, setAssets] = useState<readonly FleetVehicleAssetView[] | undefined>(undefined);
  const [linkedNow, setLinkedNow] = useState<readonly string[]>([]);
  const [driverId, setDriverId] = useState('');
  const [owner, setOwner] = useState({ email: '', name: '', phone: '' });
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    const res = await runtime.fleetManagement.listAssets(org.organizationId);
    setAssets(res.ok ? res.value.assets : []);
  }, [runtime, org.organizationId]);
  useEffect(() => {
    void load();
  }, [load]);

  const drivers = fleetDriverRows(assets ?? [], linkedNow);
  const accepting = fleetAcceptsDrivers(org);
  const classLabel = (c: string): string => (v('kyc.inspection_vehicle_class', c) === v('', 'x') ? c : v('kyc.inspection_vehicle_class', c));

  /** Linking changes how the driver is paid: the sheet says so, and nothing is sent until it is confirmed. */
  const link = async (): Promise<void> => {
    const id = driverId.trim();
    if (id === '') return;
    setMsg(undefined);
    const answer = await confirm({
      title: t('pob.fleet.link.title', { fleet: org.displayName }),
      body: t('pob.fleet.link.body', { driver: id, fleet: org.displayName }),
      confirmLabel: t('pob.fleet.link.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: 'primary',
      icon: 'shieldAlert',
    });
    if (!answer.confirmed) return;
    setBusy(true);
    const res = await runtime.fleetManagement.attachDriver(org.organizationId, { driverId: id, role: 'fleet_driver', scope: org.scope });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: res.error.code === 'CONFLICT' ? t('pob.fleet.link.refused') : explain(res.error) });
      return;
    }
    setLinkedNow((ids) => (ids.includes(id) ? ids : [...ids, id]));
    setDriverId('');
    setMsg({ tone: 'success', text: t('pob.fleet.link.done') });
    await load();
  };

  const invite = async (): Promise<void> => {
    setMsg(undefined);
    setBusy(true);
    const email = owner.email.trim().toLowerCase();
    const given = await giveOwner(org, { email, name: owner.name, phoneE164: owner.phone.replace(/[\s()-]/g, '') });
    setBusy(false);
    if (!given.ok) {
      setMsg({ tone: 'danger', text: given.text });
      return;
    }
    setOwner({ email: '', name: '', phone: '' });
    setMsg(given.link !== undefined ? { tone: 'warning', text: t('pob.fleet.owner.done.manual'), link: given.link } : { tone: 'success', text: t('pob.fleet.owner.done', { email }) });
  };

  return (
    <>
      <DrawerHeader titleId="fleet-detail-title" title={org.displayName} subtitle={<Badge tone={ORG_TONE[org.status] ?? 'neutral'}>{v('fleet.org_status', org.status)}</Badge>} onClose={onClose} closeLabel={t('ui.close')} />
      <DrawerBody>
        <DrawerStack gap={6}>
          <IdRef value={org.organizationId} copy full />
          {marketLabel !== '' && <Text role="caption" tone="secondary" as="p">{marketLabel}</Text>}
          {msg !== undefined && (
            <Notice tone={msg.tone}>
              {msg.text}
              {msg.link !== undefined && <Text as="code" mono block data-testid="fleet-owner-link">{msg.link}</Text>}
            </Notice>
          )}

          {canInviteOwner && (
            <DrawerSection title={t('pob.fleet.drawer.owner')}>
              <DrawerStack gap={3}>
                <Text role="caption" tone="secondary" as="p">{t('pob.fleet.owner.hint')}</Text>
                <Field label={t('pob.fleet.owner.email')} htmlFor="fleet-detail-owner-email"><TextInput id="fleet-detail-owner-email" type="email" autoComplete="off" value={owner.email} onChange={(e) => setOwner((o) => ({ ...o, email: e.target.value }))} /></Field>
                <Field label={t('pob.fleet.owner.name')} htmlFor="fleet-detail-owner-name" hint={t('pob.fleet.owner.newOnly')}><TextInput id="fleet-detail-owner-name" value={owner.name} onChange={(e) => setOwner((o) => ({ ...o, name: e.target.value }))} /></Field>
                <Field label={t('pob.fleet.owner.phone')} htmlFor="fleet-detail-owner-phone" hint={t('pob.fleet.owner.newOnly')}><TextInput id="fleet-detail-owner-phone" type="tel" className="tnum" value={owner.phone} onChange={(e) => setOwner((o) => ({ ...o, phone: e.target.value }))} /></Field>
                <div>
                  <Button variant="secondary" disabled={busy || owner.email.trim() === ''} onClick={() => void invite()} data-testid="fleet-owner-invite">{t('pob.fleet.owner.invite')}</Button>
                </div>
              </DrawerStack>
            </DrawerSection>
          )}

          <DrawerSection title={t('pob.fleet.drivers')}>
            <DrawerStack gap={3}>
              <Text role="caption" tone="secondary" as="p">{t('pob.fleet.drivers.note')}</Text>
              <Table
                columns={[
                  { key: 'driver', header: t('pob.fleet.col.driver'), render: (d: FleetDriverRow) => <IdRef value={d.driverId} copy /> },
                  { key: 'vehicles', header: t('pob.fleet.col.vehicles'), render: (d: FleetDriverRow) => <span className="tnum">{d.vehicles}</span> },
                ]}
                rows={drivers}
                rowKey={(d) => d.driverId}
                empty={<EmptyCell>{assets === undefined ? t('companies.loading') : t('pob.fleet.drivers.empty')}</EmptyCell>}
              />
              {canManage && (accepting ? (
                <DrawerStack gap={3}>
                  <Field label={t('pob.fleet.link.id')} htmlFor="fleet-link-driver" hint={t('pob.fleet.link.hint')}><TextInput id="fleet-link-driver" className="tnum" autoComplete="off" value={driverId} onChange={(e) => setDriverId(e.target.value)} /></Field>
                  <div>
                    <Button variant="secondary" disabled={busy || driverId.trim() === ''} onClick={() => void link()} data-testid="fleet-link-send">{t('pob.fleet.link')}</Button>
                  </div>
                </DrawerStack>
              ) : (
                <Notice tone="neutral">{t('pob.fleet.link.closed')}</Notice>
              ))}
            </DrawerStack>
          </DrawerSection>

          <DrawerSection title={t('pob.fleet.vehicles')}>
            <Table
              columns={[
                { key: 'class', header: t('pob.fleet.col.class'), render: (a: FleetVehicleAssetView) => classLabel(a.vehicleClass) },
                { key: 'status', header: t('companies.col.status'), render: (a: FleetVehicleAssetView) => <Badge tone={a.status === 'active' ? 'success' : 'neutral'}>{v('fleet.asset_status', a.status)}</Badge> },
                { key: 'driver', header: t('pob.fleet.col.assigned'), render: (a: FleetVehicleAssetView) => (a.assignedDriverId !== undefined ? <IdRef value={a.assignedDriverId} /> : '—') },
              ]}
              rows={assets ?? []}
              rowKey={(a) => a.assetId}
              empty={<EmptyCell>{assets === undefined ? t('companies.loading') : t('pob.fleet.vehicles.empty')}</EmptyCell>}
            />
          </DrawerSection>
        </DrawerStack>
      </DrawerBody>
      <DrawerFooter>
        <Button variant="ghost" onClick={onClose}>{t('ui.close')}</Button>
      </DrawerFooter>
    </>
  );
}
